/* 261 (T655, Toni 25.10.2026 klo 11.45): puutteen korvaava kuittaus — ydinkomponentti, jota Puutteet-näkymä ja moduulien
   näkymät käyttävät. Laji, perustelu ja liite ovat pakollisia, ja palvelin kirjoittaa liitteen, korvausrivin ja tilan
   `korvattu` samassa transaktiossa. Näkyy vain, kun palvelin sanoo puutteen korvattavaksi (`korvattavissa`). */
import { useState } from "react";
import { api, ApiError, AINEISTOLAJIT, type Aineistolaji, type Kayttaja, type Puute } from "./api";
import { nayttonimi } from "./nayttonimi";
import { naytaPvm } from "./pvm";
import { t, virheTeksti } from "./kieli";

/** Sama ehto kuin reitin portti (api/src/puutekorvaus.ts KORVAAJAT): työnjohto ja laatu, ei ulkopuolinen tunnus. */
export function saaKorvata(k: Pick<Kayttaja, "roolit" | "laji">): boolean {
  return k.laji !== "ulkopuolinen" && k.roolit.some((r) => r === "paakayttaja" || r === "tyonjohtaja" || r === "tarkastaja");
}

const lueDataUrl = (f: File) => new Promise<string>((res, rej) => {
  const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("luku")); r.readAsDataURL(f);
});

/** «Korvaava aineisto…» -nappi ja lomake avoimelle, korvattavalle puutteelle. */
export function KorvaavaKuittaus({ puute, muuttui }: { puute: Puute; muuttui: () => void }) {
  const [auki, setAuki] = useState(false);
  const [laji, setLaji] = useState<Aineistolaji>("mallinakyma");
  const [peruste, setPeruste] = useState("");
  const [tiedosto, setTiedosto] = useState<File | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);
  if (!puute.korvattavissa) return null;
  if (!auki) {
    return <button type="button" className="nappi nappi--kevyt" data-korvaus-avaa={puute.id} onClick={() => setAuki(true)}>{t("pu.korvaa")}</button>;
  }
  async function tallenna() {
    if (peruste.trim().length < 5 || !tiedosto) { setVirhe(t("pu.korvaa_puuttuu")); return; }
    setKesken(true); setVirhe(null);
    try {
      await api.korvaaPuute(puute.id, { aineistolaji: laji, peruste: peruste.trim(), liite: { nimi: tiedosto.name, sisalto: await lueDataUrl(tiedosto) } });
      setAuki(false); muuttui();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pu.korvaa_puuttuu"));
    } finally { setKesken(false); }
  }
  return (
    <div data-korvaus-lomake={puute.id} style={{ marginTop: 6, padding: 8, border: "1px solid var(--viiva)", maxWidth: 420, whiteSpace: "normal" }}>
      <strong>{t("pu.korvaa_otsikko")}</strong>
      <label style={{ display: "block", marginTop: 6 }}>{t("pu.korvaa_laji")}{" "}
        <select value={laji} data-korvaus-laji="" onChange={(e) => setLaji(e.target.value as Aineistolaji)}>
          {AINEISTOLAJIT.map((l) => <option key={l} value={l}>{nayttonimi("aineistolaji", l)}</option>)}
        </select>
      </label>
      <label style={{ display: "block", marginTop: 6 }}>{t("pu.korvaa_peruste")}
        <textarea rows={2} style={{ display: "block", width: "100%" }} value={peruste} data-korvaus-peruste=""
                  onChange={(e) => setPeruste(e.target.value)} />
      </label>
      <label style={{ display: "block", marginTop: 6 }}>{t("pu.korvaa_liite")}
        <input type="file" style={{ display: "block" }} data-korvaus-liite="" onChange={(e) => setTiedosto(e.target.files?.[0] ?? null)} />
      </label>
      <p className="dim" style={{ margin: "6px 0" }}>{t("pu.korvaa_vihje")}</p>
      {virhe && <p className="huom" role="alert" data-korvaus-virhe="">{virhe}</p>}
      <button type="button" className="nappi nappi--paa" disabled={kesken} data-korvaus-tallenna="" onClick={() => void tallenna()}>{t("pu.korvaa_tallenna")}</button>{" "}
      <button type="button" className="nappi nappi--kevyt" onClick={() => { setAuki(false); setVirhe(null); }}>{t("hl.peruuta")}</button>
    </div>
  );
}

/** Korvatun puutteen merkintä: «Korvattu: <laji>», kuittaaja, päivä, perustelu ja liite. */
export function KorvausMerkinta({ puute }: { puute: Puute }) {
  const k = puute.korvaus;
  if (puute.tila !== "korvattu" || !k) return null;
  return (
    <div data-korvaus-merkinta={puute.id} style={{ marginTop: 2 }}>
      <span data-korvaus-laji-teksti={k.laji}>{t("pu.korvattu_n", { laji: nayttonimi("aineistolaji", k.laji) })}</span>
      <div className="dim">{t("pu.korvattu_kuka", { nimi: k.kuittaaja, pvm: naytaPvm(k.kuitattu) ?? "", peruste: k.peruste })}{" "}
        <a href={`/api/liitteet/${k.liite_id}/tiedosto`} target="_blank" rel="noreferrer">{t("pu.korvattu_liite")}</a></div>
    </div>
  );
}
